import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  AlertIcon,
  Heading,
  Icon,
  Divider,
  IconButton,
  Collapse,
  useDisclosure,
  Flex,
  Button,
} from '@chakra-ui/react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';
import { getUserInfo } from '../auth/keycloak';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { formatVietnameseTimeAgo } from '../utils/time';
import {
  FaChalkboard,
  FaClipboardList,
  FaCheckCircle,
  FaUserPlus,
  FaChevronDown,
  FaChevronUp,
  FaCircle,
  FaUsers,
} from 'react-icons/fa';

// Hash-based state management (survives navigation, reload, back/forward)
const STORAGE_KEY = 'activityFeedPreferences';

interface CardPreferences {
  expandedStates: Record<string, boolean>;
}

function loadCardPreferencesFromHash(): Record<string, boolean> {
  console.log('📖 [loadCardPreferencesFromHash] Starting, current hash:', window.location.hash);
  try {
    // First check URL hash
    let hash = window.location.hash;
    console.log('📖 [loadCardPreferencesFromHash] Initial hash from URL:', hash);
    
    // If no hash in URL, check sessionStorage (backup from Keycloak redirects)
    if (!hash) {
      const savedHash = sessionStorage.getItem('activityFeedHash');
      console.log('📖 [loadCardPreferencesFromHash] No URL hash, checking sessionStorage:', savedHash);
      if (savedHash) {
        hash = savedHash;
        // Restore to URL
        window.location.hash = savedHash;
        console.log('🔄 [loadCardPreferencesFromHash] Restored hash from sessionStorage:', savedHash);
      }
    }
    
    if (hash.startsWith('#expanded=')) {
      const expandedCards = hash.replace('#expanded=', '').split(',').filter(Boolean);
      const result = expandedCards.reduce((acc, cardId) => ({ ...acc, [cardId]: true }), {});
      console.log('✅ [loadCardPreferencesFromHash] Parsed hash result:', result);
      return result;
    } else {
      console.log('⚠️ [loadCardPreferencesFromHash] Hash does not start with #expanded=, returning empty');
    }
  } catch (error) {
    console.error('❌ [loadCardPreferencesFromHash] Failed to parse hash:', error);
  }
  console.log('📖 [loadCardPreferencesFromHash] Returning empty object');
  return {};
}

function loadCardPreferences(): CardPreferences | null {
  try {
    // Priority 1: Read from URL hash
    const hashStates = loadCardPreferencesFromHash();
    if (Object.keys(hashStates).length > 0) {
      return { expandedStates: hashStates };
    }
    
    // Priority 2: Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load card preferences:', error);
    return null;
  }
}

function saveCardPreferences(preferences: CardPreferences, programmaticFlagRef?: React.MutableRefObject<boolean>): void {
  console.log('💾 [saveCardPreferences] Called with preferences:', preferences);
  console.log('💾 [saveCardPreferences] Current hash before save:', window.location.hash);
  try {
    // Save to URL hash
    const expandedCards = Object.entries(preferences.expandedStates)
      .filter(([_, isExpanded]) => isExpanded)
      .map(([cardId]) => cardId);
    
    console.log('💾 [saveCardPreferences] Expanded cards:', expandedCards);
    
    if (expandedCards.length > 0) {
      const newHash = `expanded=${expandedCards.join(',')}`;
      const expectedFullHash = `#${newHash}`;
      
      console.log('💾 [saveCardPreferences] Desired hash:', newHash);
      console.log('💾 [saveCardPreferences] Current full hash:', window.location.hash);
      
      // Only set hash if it's actually different to prevent triggering hashchange unnecessarily
      if (window.location.hash !== expectedFullHash) {
        console.log('💾 [saveCardPreferences] Hash different, setting new hash');
        // Set flag BEFORE changing hash to prevent hashchange listener from triggering
        if (programmaticFlagRef) {
          programmaticFlagRef.current = true;
          console.log('🚩 [saveCardPreferences] Set programmatic flag to true');
        }
        window.location.hash = newHash;
        // Also save to sessionStorage as backup for Keycloak redirects
        sessionStorage.setItem('activityFeedHash', expectedFullHash);
        console.log('✅ [saveCardPreferences] Hash saved to URL and sessionStorage');
      } else {
        console.log('⏭️ [saveCardPreferences] Hash unchanged, skipping set (prevent hashchange loop)');
        // Still update sessionStorage even if hash unchanged
        sessionStorage.setItem('activityFeedHash', expectedFullHash);
      }
    } else {
      console.log('🗑️ [saveCardPreferences] No expanded cards, clearing hash');
      
      // Only clear if hash exists to prevent triggering hashchange unnecessarily
      if (window.location.hash !== '') {
        console.log('💾 [saveCardPreferences] Hash exists, clearing it');
        // Set flag BEFORE clearing hash
        if (programmaticFlagRef) {
          programmaticFlagRef.current = true;
          console.log('🚩 [saveCardPreferences] Set programmatic flag to true');
        }
        window.location.hash = '';
        sessionStorage.removeItem('activityFeedHash');
      } else {
        console.log('⏭️ [saveCardPreferences] Hash already empty, skipping clear');
        sessionStorage.removeItem('activityFeedHash');
      }
    }
    
    // Also save to localStorage as backup
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    console.log('💾 [saveCardPreferences] Saved to localStorage');
  } catch (error) {
    console.error('❌ [saveCardPreferences] Failed to save:', error);
  }
}

interface Activity {
  id: number;
  type: string;
  actor: {
    id: number;
    name: string;
    avatarId?: number;
  };
  entity: {
    type: string;
    id: number;
    title: string;
    code: string;
  };
  classroom?: {
    id: number;
    name: string;
    code: string;
  };
  createdAt: string;
  metadata?: any;
}

interface ActivityFeedData {
  activities: Activity[];
  total: number;
  limit: number;
  skip: number;
  hasMore: boolean;
  nextSkip: number;
}

interface OnlineUser {
  userId: number;
  userName: string;
  displayName: string;
  highestRole: string;
  lastActivityAt: string;
}

interface OnlineUsersData {
  users: OnlineUser[];
  totalOnline: number;
  studentsOnline: number;
  teachersOnline: number;
}

// Group similar activities that happened close together
function groupActivities(activities: Activity[]): Array<{activity: Activity, count: number}> {
  const grouped: Map<string, {activity: Activity, count: number}> = new Map();
  
  activities.forEach(activity => {
    // Group by: actor + entity + type within 10 minutes
    const key = `${activity.actor.id}_${activity.entity.id}_${activity.type}`;
    const existing = grouped.get(key);
    
    if (existing) {
      const timeDiff = new Date(existing.activity.createdAt).getTime() - new Date(activity.createdAt).getTime();
      // If within 10 minutes, group them
      if (Math.abs(timeDiff) <= 10 * 60 * 1000) {
        existing.count++;
        return;
      }
    }
    
    grouped.set(key, { activity, count: 1 });
  });
  
  return Array.from(grouped.values());
}

/**
 * Activity Feed Component - Shows recent activities in separate cards by category
 * Displayed on home page, filtered by user role
 */
export default function RecentActivityFeed(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [expandAll, setExpandAll] = useState(false);
  const navigate = useNavigate();

  // Get user role from token - memoize to prevent recalculation
  const userInfo = React.useMemo(() => getUserInfo(), []);
  const isAdmin = userInfo?.roles?.includes('ADMIN') ?? false;
  const isTeacher = userInfo?.roles?.includes('TEACHER') ?? false;
  const isStudent = !isAdmin && !isTeacher;

  // Define activity categories based on user role
  const activityCategories = React.useMemo(() => {
    const categories = [];
    
    // Classroom activities - only for admin and teacher
    if (isAdmin || isTeacher) {
      categories.push({
        key: 'classroom',
        title: t('activity.category.classroom'),
        types: ['CLASSROOM_CREATED'],
        icon: FaChalkboard,
        colorScheme: 'purple',
      });
    }
    
    // Assignment activities - all users can see
    categories.push({
      key: 'assignment',
      title: t('activity.category.assignment'),
      types: ['ASSIGNMENT_CREATED', 'ASSIGNMENT_PUBLISHED'],
      icon: FaClipboardList,
      colorScheme: 'blue',
    });
    
    // Submission activities - all users can see (filtered by backend)
    categories.push({
      key: 'submission',
      title: t('activity.category.submission'),
      types: ['SUBMISSION_CREATED', 'SUBMISSION_GRADED'],
      icon: FaCheckCircle,
      colorScheme: 'green',
    });
    
    // Enrollment activities - only for admin and teacher
    if (isAdmin || isTeacher) {
      categories.push({
        key: 'enrollment',
        title: t('activity.category.enrollment'),
        types: ['ENROLLMENT_ADDED'],
        icon: FaUserPlus,
        colorScheme: 'orange',
      });
    }
    
    return categories;
  }, [isAdmin, isTeacher, t]);

  const isVisible = usePageVisibility();

  // Separate infinite queries for each activity category
  interface CategoryQueryData {
    items: Activity[];
    total: number;
    hasMore: boolean;
  }

  const classroomsQuery = useInfiniteQuery<CategoryQueryData>({
    queryKey: ['activity-classrooms'],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      params.set('limit', '10');
      params.set('skip', String(pageParam));
      return apiGet<CategoryQueryData>(`/api/activity/classrooms?${params.toString()}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return lastPage.hasMore ? loadedCount : undefined;
    },
    refetchInterval: isVisible ? 60000 : false,
    enabled: isVisible,
    staleTime: 30000,
    initialPageParam: 0,
  });

  const assignmentsQuery = useInfiniteQuery<CategoryQueryData>({
    queryKey: ['activity-assignments'],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      params.set('limit', '10');
      params.set('skip', String(pageParam));
      return apiGet<CategoryQueryData>(`/api/activity/assignments?${params.toString()}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return lastPage.hasMore ? loadedCount : undefined;
    },
    refetchInterval: isVisible ? 60000 : false,
    enabled: isVisible,
    staleTime: 30000,
    initialPageParam: 0,
  });

  const submissionsQuery = useInfiniteQuery<CategoryQueryData>({
    queryKey: ['activity-submissions'],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      params.set('limit', '10');
      params.set('skip', String(pageParam));
      return apiGet<CategoryQueryData>(`/api/activity/submissions?${params.toString()}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return lastPage.hasMore ? loadedCount : undefined;
    },
    refetchInterval: isVisible ? 60000 : false,
    enabled: isVisible,
    staleTime: 30000,
    initialPageParam: 0,
  });

  const enrollmentsQuery = useInfiniteQuery<CategoryQueryData>({
    queryKey: ['activity-enrollments'],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      params.set('limit', '10');
      params.set('skip', String(pageParam));
      return apiGet<CategoryQueryData>(`/api/activity/enrollments?${params.toString()}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return lastPage.hasMore ? loadedCount : undefined;
    },
    refetchInterval: isVisible ? 60000 : false,
    enabled: isVisible,
    staleTime: 30000,
    initialPageParam: 0,
  });

  // Map queries to categories (keys must match category.key)
  const categoryQueries: Record<string, typeof classroomsQuery> = {
    'classroom': classroomsQuery,
    'assignment': assignmentsQuery,
    'submission': submissionsQuery,
    'enrollment': enrollmentsQuery,
  };

  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(() => {
    console.log('🎬 [useState init] Initializing expandedStates');
    // Load from URL hash or localStorage
    const preferences = loadCardPreferences();
    const initialState = preferences?.expandedStates || {};
    console.log('🎬 [useState init] Initial state:', initialState);
    return initialState;
  });

  // Flag to prevent hashchange listener from triggering when we set hash programmatically
  const isSettingHashProgrammatically = React.useRef(false);

  // Listen to hash changes (for browser back/forward)
  useEffect(() => {
    console.log('🔗 [hashchange useEffect] Setting up hashchange listener');
    const handleHashChange = () => {
      // Skip if we just set the hash ourselves (not from browser back/forward)
      if (isSettingHashProgrammatically.current) {
        console.log('⏭️ [hashchange] Skipping - hash set programmatically');
        isSettingHashProgrammatically.current = false;
        return;
      }
      
      console.log('🔗 [hashchange] Event fired! Old hash:', window.location.hash);
      const hashStates = loadCardPreferencesFromHash();
      console.log('🔗 [hashchange] Loaded hash states:', hashStates);
      // Only update if hash actually has expansion data
      // Don't clear state if hash is empty (could be user navigating to #something-else)
      if (Object.keys(hashStates).length > 0) {
        console.log('🔗 [hashchange] Updating expandedStates to:', hashStates);
        setExpandedStates(hashStates);
      } else {
        console.log('⚠️ [hashchange] Hash states empty, NOT updating expandedStates');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    console.log('🔗 [hashchange useEffect] Listener attached');
    return () => {
      console.log('🔗 [hashchange useEffect] Cleaning up listener');
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Save expanded states to hash and storage whenever they change
  const prevExpandedStatesRef = React.useRef<Record<string, boolean>>({});
  useEffect(() => {
    console.log('💿 [save useEffect] Triggered with expandedStates:', expandedStates);
    // Only save if state actually changed (prevent infinite loop)
    const prevState = prevExpandedStatesRef.current;
    console.log('💿 [save useEffect] Previous state:', prevState);
    const hasChanged = JSON.stringify(prevState) !== JSON.stringify(expandedStates);
    console.log('💿 [save useEffect] Has changed?', hasChanged);
    
    if (hasChanged) {
      console.log('💿 [save useEffect] Calling saveCardPreferences');
      saveCardPreferences({ expandedStates }, isSettingHashProgrammatically);
      prevExpandedStatesRef.current = expandedStates;
    } else {
      console.log('💿 [save useEffect] No change detected, skipping save');
    }
  }, [expandedStates]);

  // Handle expand/collapse for specific card
  const toggleCard = (cardId: string, isExpanded: boolean) => {
    console.log('🎯 [toggleCard] Called for card:', cardId, 'isExpanded:', isExpanded);
    console.log('🎯 [toggleCard] Current expandedStates before update:', expandedStates);
    setExpandedStates(prev => {
      const newState = { ...prev, [cardId]: isExpanded };
      console.log('🎯 [toggleCard] New state:', newState);
      return newState;
    });
  };

  // Apply expandAll toggle - ONLY when user explicitly toggles it
  const prevExpandAllRef = React.useRef(expandAll);
  useEffect(() => {
    // Only update if expandAll actually changed (not on initial mount or activityCategories change)
    if (prevExpandAllRef.current !== expandAll) {
      console.log('🔄 [expandAll useEffect] expandAll changed from', prevExpandAllRef.current, 'to', expandAll);
      const cardIds = isAdmin ? ['online', ...activityCategories.map(c => c.key)] : activityCategories.map(c => c.key);
      if (expandAll) {
        const allExpanded = cardIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
        setExpandedStates(allExpanded);
      } else {
        const allCollapsed = cardIds.reduce((acc, id) => ({ ...acc, [id]: false }), {});
        setExpandedStates(allCollapsed);
      }
      prevExpandAllRef.current = expandAll;
    } else {
      console.log('⏭️ [expandAll useEffect] expandAll unchanged, skipping state update');
    }
  }, [expandAll, activityCategories, isAdmin]);

  return (
    <Box>
      <HStack justifyContent="space-between" mb={4}>
        <Heading size="md">{t('activity.recentActivities')}</Heading>
        <IconButton
          aria-label={expandAll ? 'Collapse all' : 'Expand all'}
          icon={expandAll ? <FaChevronUp /> : <FaChevronDown />}
          size="sm"
          variant="ghost"
          onClick={() => setExpandAll(!expandAll)}
        />
      </HStack>
      
      <VStack spacing={4}
        align="stretch"
      >
        {/* Online Users Card - Admin only */}
        {isAdmin && (
          <OnlineUsersCard 
            locale={i18n.language} 
            isExpanded={expandedStates['online'] ?? false}
            onToggle={(isExpanded) => toggleCard('online', isExpanded)}
          />
        )}

        {/* Activity Category Cards with separate queries */}
        {activityCategories.map((category) => {
          const query = categoryQueries[category.key];
          
          if (!query) {
            console.warn(`⚠️ No query found for category: ${category.key}`);
            return null;
          }
          
          return (
            <ActivityCategoryCard
              key={category.key}
              category={category}
              locale={i18n.language}
              query={query}
              isExpanded={expandedStates[category.key] ?? false}
              onToggle={(isExpanded) => toggleCard(category.key, isExpanded)}
              isStudent={isStudent}
            />
          );
        })}
      </VStack>
    </Box>
  );
}

/**
 * Online Users Card - Shows currently online users
 */
interface OnlineUsersCardProps {
  locale: string;
  isExpanded: boolean;
  onToggle: (isExpanded: boolean) => void;
}

function OnlineUsersCard({ locale, isExpanded, onToggle }: OnlineUsersCardProps): JSX.Element {
  const { t } = useTranslation();
  const isVisible = usePageVisibility();

  // Fetch online users (pause when tab is hidden)
  const { data, isLoading, error } = useQuery<OnlineUsersData>({
    queryKey: ['online-users'],
    queryFn: () => apiGet<OnlineUsersData>('/api/activity/online-users'),
    refetchInterval: isVisible ? 30000 : false, // Only refresh when tab is visible
    enabled: isVisible, // Pause query when tab is hidden
  });

  const users = data?.users || [];
  const totalOnline = data?.totalOnline || 0;
  const studentsOnline = data?.studentsOnline || 0;
  const teachersOnline = data?.teachersOnline || 0;

  return (
    <Card 
      display="flex" 
      flexDirection="column"
      maxH={isExpanded ? "480px" : "auto"}
      transition="max-height 0.2s"
    >
      <CardHeader 
        pb={2}
        cursor="pointer"
        onClick={() => onToggle(!isExpanded)}
        _hover={{ bg: 'gray.50' }}
        transition="background 0.2s"
      >
        <HStack spacing={3}>
          <Box
            bg="teal.100"
            p={2}
            borderRadius="md"
            color="teal.600"
          >
            <Icon as={FaUsers} boxSize={5} />
          </Box>
          <Box flex="1">
            <Heading size="sm">{t('activity.onlineUsers')}</Heading>
            <HStack spacing={2} mt={1}>
              <Badge colorScheme="teal" size="sm">{totalOnline} tổng</Badge>
              <Badge colorScheme="blue" size="sm">{teachersOnline} GV</Badge>
              <Badge colorScheme="gray" size="sm">{studentsOnline} HS</Badge>
            </HStack>
          </Box>
          <Icon as={isExpanded ? FaChevronUp : FaChevronDown} boxSize={4} />
        </HStack>
      </CardHeader>

      <Collapse in={isExpanded} animateOpacity>
        <Divider />
        <CardBody flex="1" overflow="hidden" display="flex" flexDirection="column">
          {isLoading ? (
            <VStack spacing={3} py={4}>
              <Spinner size="md" color="teal.500" />
              <Text fontSize="sm" color="gray.600">{t('activity.loading')}</Text>
            </VStack>
          ) : error ? (
            <Alert status="error" size="sm">
              <AlertIcon />
              {t('activity.loadError')}
            </Alert>
          ) : users.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Text fontSize="sm" color="gray.500">{t('activity.noOnlineUsers')}</Text>
            </Box>
          ) : (
            <VStack 
              spacing={2} 
              align="stretch" 
              overflowY="auto" 
              maxH="220px"
              pr={2}
              css={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#cbd5e0',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#a0aec0',
                },
              }}
            >
              {users.map((user) => (
                <OnlineUserItem key={user.userId} user={user} locale={locale} />
              ))}
            </VStack>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
}

/**
 * Online User Item
 */
interface OnlineUserItemProps {
  user: OnlineUser;
  locale: string;
}

function OnlineUserItem({ user, locale }: OnlineUserItemProps): JSX.Element {
  const relativeTime = locale === 'vi' 
    ? formatVietnameseTimeAgo(new Date(user.lastActivityAt))
    : formatDistanceToNow(new Date(user.lastActivityAt), {
        addSuffix: true,
        locale: enUS,
      });

  const userProfileLink = `/users/${user.userId}`;

  return (
    <Link to={userProfileLink} style={{ textDecoration: 'none', display: 'block' }}>
      <HStack
        spacing={2}
        p={2}
        borderRadius="md"
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        _hover={{ bg: 'gray.50', borderColor: 'teal.300' }}
        transition="all 0.2s"
        cursor="pointer"
      >
        <Icon as={FaCircle} boxSize={2} color="green.500" />
        <Avatar
          size="xs"
          name={user.displayName}
        />
        <Box flex={1} minW={0}>
          <Text fontSize="xs" fontWeight="medium" color="gray.700" noOfLines={1}>
            {user.displayName}
          </Text>
          <Text fontSize="xs" color="gray.400" noOfLines={1}>
            {relativeTime}
          </Text>
        </Box>
        <Badge size="xs" colorScheme={user.highestRole === 'TEACHER' ? 'blue' : 'gray'}>
          {user.highestRole}
        </Badge>
      </HStack>
    </Link>
  );
}

/**
 * Activity Category Card - Shows activities for specific category
 */
interface ActivityCategoryCardProps {
  category: {
    key: string;
    title: string;
    types: string[];
    icon: any;
    colorScheme: string;
  };
  locale: string;
  query: {
    data: any;
    isLoading: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
  };
  isExpanded: boolean;
  onToggle: (isExpanded: boolean) => void;
  isStudent: boolean;
}

function ActivityCategoryCard({ category, locale, query, isExpanded, onToggle, isStudent }: ActivityCategoryCardProps): JSX.Element {
  const { t } = useTranslation();

  console.log(`🎴 [ActivityCategoryCard:${category.key}] Rendering, isExpanded:`, isExpanded);

  // Flatten all pages into activities array
  const activities = React.useMemo(() => {
    if (!query.data) return [];
    return query.data.pages.flatMap((page: any) => page.items);
  }, [query.data]);

  const total = query.data?.pages[0]?.total || 0;
  const hasMore = query.hasNextPage;

  console.log(`🎴 [ActivityCategoryCard:${category.key}] Activities:`, activities.length, 'Total:', total, 'HasMore:', hasMore, 'Query Data:', query.data);
  
  // Don't limit groupedActivities - show all loaded activities
  const groupedActivities = groupActivities(activities);
  console.log(`🎴 [ActivityCategoryCard:${category.key}] Grouped:`, groupedActivities.length);

  // Calculate summary statistics
  const uniqueUsers = new Set(activities.map(a => a.actor.id)).size;
  const uniqueEntities = new Set(activities.map(a => a.entity.id)).size;
  const recentCount = activities.filter(a => {
    const diffMinutes = (new Date().getTime() - new Date(a.createdAt).getTime()) / (1000 * 60);
    return diffMinutes < 60;
  }).length;

  return (
    <Card 
      display="flex" 
      flexDirection="column"
      maxH={isExpanded ? "480px" : "auto"}
      transition="max-height 0.2s"
    >
      <CardHeader 
        pb={2}
        cursor="pointer"
        onClick={() => onToggle(!isExpanded)}
        _hover={{ bg: 'gray.50' }}
        transition="background 0.2s"
      >
        <HStack spacing={3}>
          <Box
            bg={`${category.colorScheme}.100`}
            p={2}
            borderRadius="md"
            color={`${category.colorScheme}.600`}
          >
            <Icon as={category.icon} boxSize={5} />
          </Box>
          <Box flex="1">
            <Heading size="sm">{category.title}</Heading>
            <HStack spacing={2} mt={1}>
              <Badge colorScheme={category.colorScheme} size="sm">{activities.length} hoạt động</Badge>
              {uniqueUsers > 0 && <Badge colorScheme="purple" size="sm">{uniqueUsers} người</Badge>}
              {uniqueEntities > 0 && <Badge colorScheme="gray" size="sm">{uniqueEntities} mục</Badge>}
              {recentCount > 0 && <Badge colorScheme="red" size="sm">{recentCount} mới (1h)</Badge>}
            </HStack>
          </Box>
          <Icon as={isExpanded ? FaChevronUp : FaChevronDown} boxSize={4} />
        </HStack>
      </CardHeader>

      <Collapse in={isExpanded} transition={{ enter: { duration: 0.15 }, exit: { duration: 0.15 } }}>
        <Box>
          <Divider />
          <CardBody>
            {query.isLoading ? (
              <VStack spacing={3} py={4}>
                <Spinner size="md" color={`${category.colorScheme}.500`} />
                <Text fontSize="sm" color="gray.600">{t('activity.loading')}</Text>
              </VStack>
            ) : groupedActivities.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Text fontSize="sm" color="gray.500">{t('activity.noActivities')}</Text>
              </Box>
            ) : (
              <Box
                overflowY="auto" 
                maxH="380px"
                pr={2}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#cbd5e0',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a0aec0',
                  },
                }}
              >
                <VStack spacing={2} align="stretch">
                  {groupedActivities.map(({ activity, count }, index) => (
                    <ActivityItemCompact 
                      key={`${activity.type}_${activity.entity.id}_${activity.createdAt}_${index}`}
                      activity={activity} 
                      count={count}
                      locale={locale}
                      colorScheme={category.colorScheme}
                      isStudent={isStudent}
                    />
                  ))}
                  
                  {/* Load More Button as last item in list */}
                  {query.hasNextPage && (
                    <Box pt={3} borderTop="1px" borderColor="gray.200" bg="yellow.50" p={3} borderRadius="md">
                      <Flex justify="center">
                        <Button
                          onClick={() => {
                            console.log(`🔘 [${category.key}] Load More clicked!`);
                            query.fetchNextPage();
                          }}
                          isLoading={query.isFetchingNextPage}
                          loadingText={t('common.loading')}
                          colorScheme={category.colorScheme}
                          variant="outline"
                          size="sm"
                          leftIcon={<FaChevronDown />}
                        >
                          {query.isFetchingNextPage 
                            ? t('activity.loadingMore') 
                            : `Tải thêm (${total - activities.length} còn lại)`}
                        </Button>
                      </Flex>
                      <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
                        Hiển thị {activities.length} / {total}
                      </Text>
                    </Box>
                  )}
                  
                  {/* All loaded message as last item */}
                  {!query.hasNextPage && activities.length > 0 && (
                    <Box pt={3} borderTop="1px" borderColor="gray.200" bg="green.50" p={3} borderRadius="md">
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        ✅ Đã tải hết {total} hoạt động
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
          </CardBody>
        </Box>
      </Collapse>
    </Card>
  );
}

/**
 * Compact Activity Item - Simplified version for category cards
 */
interface ActivityItemCompactProps {
  activity: Activity;
  count: number;
  locale: string;
  colorScheme: string;
  isStudent: boolean;  // Pass from parent instead of recalculating
}

function ActivityItemCompact({ activity, count, locale, colorScheme, isStudent }: ActivityItemCompactProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get link URLs based on activity type and user role
  const getItemLink = () => {
    const assignmentCode = activity.entity.code;
    
    // For submissions
    if (activity.type === 'SUBMISSION_CREATED' || activity.type === 'SUBMISSION_GRADED') {
      const studentId = activity.actor.id;
      
      if (isStudent) {
        // Student always goes to their own practice page
        return `/student/assignments/${assignmentCode}`;
      } else {
        // Teacher/Admin goes to student's practice detail page
        return `/assignments/${assignmentCode}/students/${studentId}/practice`;
      }
    }
    
    // For assignment activities - different links for student vs teacher/admin
    if (activity.type.startsWith('ASSIGNMENT_')) {
      if (isStudent) {
        // Student goes to their practice page
        return `/student/assignments/${assignmentCode}`;
      } else {
        // Teacher/Admin goes to assignment management
        return `/assignments/${assignmentCode}`;
      }
    }

    // For classroom activities
    if (activity.type.startsWith('CLASSROOM_')) {
      return activity.classroom ? `/classrooms/${activity.classroom.code}` : '#';
    }

    // For enrollment activities
    if (activity.type === 'ENROLLMENT_ADDED') {
      return activity.classroom ? `/classrooms/${activity.classroom.code}` : '#';
    }

    // Default: link to assignment
    return isStudent ? `/student/assignments/${assignmentCode}` : `/assignments/${assignmentCode}`;
  };

  // Format relative time
  const relativeTime = locale === 'vi'
    ? formatVietnameseTimeAgo(new Date(activity.createdAt))
    : formatDistanceToNow(new Date(activity.createdAt), {
        addSuffix: true,
        locale: enUS,
      });

  // Format activity message with more detail based on type
  const getMessage = () => {
    // Submission activities
    if (activity.type === 'SUBMISSION_CREATED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã nộp bài tập{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
          {activity.classroom && (
            <>
              {' '}ở lớp{' '}
              <Text as="span" color="blue.600" fontWeight="medium">
                {activity.classroom.name}
              </Text>
            </>
          )}
          {count > 1 && (
            <Text as="span" color="gray.500" fontWeight="bold">
              {' '}({count} lần)
            </Text>
          )}
        </Text>
      );
    }

    if (activity.type === 'SUBMISSION_GRADED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã chấm bài tập{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
          {activity.classroom && (
            <>
              {' '}ở lớp{' '}
              <Text as="span" color="blue.600" fontWeight="medium">
                {activity.classroom.name}
              </Text>
            </>
          )}
        </Text>
      );
    }

    // Assignment activities
    if (activity.type === 'ASSIGNMENT_CREATED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã tạo bài tập{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
          {activity.classroom && (
            <>
              {' '}ở lớp{' '}
              <Text as="span" color="blue.600" fontWeight="medium">
                {activity.classroom.name}
              </Text>
            </>
          )}
        </Text>
      );
    }

    if (activity.type === 'ASSIGNMENT_UPDATED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã cập nhật bài tập{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
          {activity.classroom && (
            <>
              {' '}ở lớp{' '}
              <Text as="span" color="blue.600" fontWeight="medium">
                {activity.classroom.name}
              </Text>
            </>
          )}
        </Text>
      );
    }

    if (activity.type === 'ASSIGNMENT_PUBLISHED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã phát hành bài tập{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
          {activity.classroom && (
            <>
              {' '}ở lớp{' '}
              <Text as="span" color="blue.600" fontWeight="medium">
                {activity.classroom.name}
              </Text>
            </>
          )}
        </Text>
      );
    }

    // Classroom activities
    if (activity.type === 'CLASSROOM_CREATED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã tạo lớp học{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
        </Text>
      );
    }

    if (activity.type === 'CLASSROOM_UPDATED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã cập nhật lớp học{' '}
          <Text as="span" color={`${colorScheme}.600`} fontWeight="medium">
            {activity.entity.title}
          </Text>
        </Text>
      );
    }

    // Enrollment activities
    if (activity.type === 'ENROLLMENT_ADDED') {
      return (
        <Text fontSize="xs" color="gray.700" noOfLines={2}>
          đã tham gia lớp{' '}
          {activity.classroom && (
            <Text as="span" color="blue.600" fontWeight="medium">
              {activity.classroom.name}
            </Text>
          )}
        </Text>
      );
    }

    // Default fallback
    return (
      <Text
        fontSize="xs"
        color={`${colorScheme}.600`}
        noOfLines={1}
      >
        {activity.entity.title}
        {count > 1 && ` (${count}x)`}
      </Text>
    );
  };

  const itemLink = getItemLink();
  
  return (
    <HStack
      spacing={2}
      p={2}
      borderRadius="md"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      _hover={{ bg: 'gray.50', borderColor: `${colorScheme}.300` }}
      transition="all 0.2s"
      align="start"
      cursor="pointer"
      onClick={() => navigate(itemLink)}
    >
        {/* Avatar */}
        <Avatar
          size="xs"
          name={activity.actor.name}
          src={activity.actor.avatarId ? `/api/media/${activity.actor.avatarId}` : undefined}
        />

        {/* Content */}
        <Box flex={1} minW={0}>
          <Text fontSize="xs" fontWeight="medium" color="gray.700" noOfLines={1}>
            {activity.actor.name}
          </Text>

          {getMessage()}

          <Text fontSize="xs" color="gray.400" mt={0.5}>
            {relativeTime}
          </Text>
        </Box>
      </HStack>
    );
}
